import { Button, Loader, Stack, Text, Title, Tooltip, UnstyledButton } from "@mantine/core";
import { modals } from "@mantine/modals";
import { IconMap2 } from "@tabler/icons-react";
import { queryOptions, useQuery } from "@tanstack/react-query";
import { Suspense } from "react";
import { httpPostGraphql } from "shared/api/httpClient";
import { AudienceGeoCard } from "shared/components/message/AudienceGeoCard";
import { ComposeFormProvider, useComposeFormContext } from "shared/components/message/compose/context";
import { GeoAudienceEditor } from "shared/components/message/compose/geoAudience/GeoAudienceEditor";
import { useFormState } from "shared/hooks/useFormState";
import { graphql } from "src/gql";

export const AudienceGeoEditor = () => {
    const form = useComposeFormContext();
    const [geofilters, setGeofilters] = useFormState(form, "audience.geofilters");
    const [features, setFeatures] = useFormState(form, "audience.features");
    const [senderId] = useFormState(form, "sender.id");

    const { data: senderFilters = [], isPending: isSenderFiltersPending } = useQuery(queries.senderFilters(senderId));

    const hasSenderShapes = senderFilters.length > 0;
    const hasRegion = geofilters.length > 0 || features.length > 0;

    function handleOpen() {
        modals.open({
            size: "xl",
            title: <Title order={3}>Select Geography</Title>,
            children: (
                <ComposeFormProvider form={form}>
                    <GeoAudienceEditor />
                </ComposeFormProvider>
            ),
        });
    }

    return (
        <Stack>
            <div>
                <Text fw="bold">Geography</Text>
                <Text c="dimmed" size="xs">
                    Send to users whose address falls within the following boundary.
                </Text>
            </div>
            {!hasRegion && (
                <Tooltip
                    label={!senderId ? "Please select a sender first" : "Sender needs assigned shapes"}
                    disabled={!!senderId && (hasSenderShapes || isSenderFiltersPending)}
                >
                    <Button
                        disabled={!senderId || (!hasSenderShapes && !isSenderFiltersPending)}
                        variant="default"
                        leftSection={<IconMap2 size={16} />}
                        onClick={() => {
                            handleOpen();
                        }}
                    >
                        Add Region
                    </Button>
                </Tooltip>
            )}
            {hasRegion && (
                <UnstyledButton onClick={() => handleOpen()} w="fit-content">
                    <Suspense fallback={<Loader />}>
                        <AudienceGeoCard
                            onDelete={() => {
                                setFeatures([]);
                                setGeofilters([]);
                            }}
                        />
                    </Suspense>
                </UnstyledButton>
            )}
        </Stack>
    );
};

const queries = {
    senderFilters: (senderId: string) =>
        queryOptions({
            enabled: !!senderId,
            queryKey: ["sender", "filters", "compose-geo", senderId],
            queryFn: () =>
                httpPostGraphql(
                    // limit = 1 just to check if sender has any filters
                    graphql(`
                        query AudienceGeoEditorSenderFilters($senderId: UUID!) {
                            sender(id: $senderId) {
                                id
                                filters(limit: 1, offset: 0) {
                                    id
                                }
                            }
                        }
                    `),
                    { senderId },
                ),
            select: (data) => data.sender?.filters ?? [],
        }),
};
